import { inject, Injectable } from '@angular/core';
import {
  DataRemoteConfigService,
  GetLtBoxParams,
  GetLvPanelConfigParams,
  UpdateLvPanelConfigParams,
  UpdateLvPanelConfigRequest,
  UpdateLvPanelConfigResponse,
  LvPanelIdentifier,
  LvPanelConfiguration,
  Line
} from '@data';
import { Observable } from 'rxjs';
import { ErrorHandlerService } from '@errorHandler';
import { ERROR_KEY } from '../core/constants/error-messages.constants';

/**
 * RemoteConfigService handles the logic for fetching remote configuration data
 * such as lv panel identifiers and configurations.
 */
@Injectable({ providedIn: 'root' })
export class RemoteConfigService {
  protected readonly dataRemoteConfigService = inject(DataRemoteConfigService);
  protected readonly errorService = inject(ErrorHandlerService);

  /**
   * Retrieves the list of lv panel identifiers for a given center and transformer.
   * @param params - Parameters containing centerId and transformerId.
   * @returns An Observable emitting an array of LvPanelIdentifier.
   */
  getLvPanelIdentifiers(params: GetLtBoxParams): Observable<LvPanelIdentifier[]> {
    return this.dataRemoteConfigService.getLtBox(params).pipe(
      this.errorService.handleError<LvPanelIdentifier[]>({
        errorKey: ERROR_KEY('ERROR_LT_BOX'),
        fallbackValue: [],
      }),
    );
  }

  /**
   * Retrieves the configuration details for a specific lv panel.
   * @param params - Parameters containing lvPanelId, centerId and transformerId.
   * @returns An Observable emitting the LvPanelConfiguration.
   */
  getLvPanelConfiguration(params: GetLvPanelConfigParams): Observable<LvPanelConfiguration | null> {
    return this.dataRemoteConfigService.getLvPanelConfig(params).pipe(
      this.errorService.handleError<LvPanelConfiguration | null>({
        errorKey: ERROR_KEY('ERROR_LT_BOX_CONFIG'),
        fallbackValue: null,
      }),
    );
  }

  /**
   * Updates the configuration for a specific lv panel.
   * @param params - Parameters containing centerId.
   * @param config - The configuration data to update.
   * @returns An Observable emitting the update response.
   */
  updateLvPanelConfiguration(params: UpdateLvPanelConfigParams, config: UpdateLvPanelConfigRequest): Observable<UpdateLvPanelConfigResponse> {
    return this.dataRemoteConfigService.updateLvPanelConfig(params, config).pipe(
      this.errorService.handleError<UpdateLvPanelConfigResponse>({
        errorKey: ERROR_KEY('ERROR_UPDATE_LT_BOX_CONFIG'),
        fallbackValue: null,
      }),
    );
  }

  /**
   * Retrieves the list of lines.
   * @param params - Parameters containing distributorId.
   * @returns An Observable emitting an array of Line objects.
   */
  getLines(distributorIds: string,): Observable<Line[]> {
    const params = { distributorIds: distributorIds };
    return this.dataRemoteConfigService.getLines(params).pipe(
      this.errorService.handleError<Line[]>({
        errorKey: ERROR_KEY('ERROR_LINES_LIST'),
        fallbackValue: [],
      }),
    );
  }
}
